import {
  client,
  params,
  arrayOf,
  bool,
  shape,
  string,
  number,
  customType
} from '@thd-nucleus/data-sources';

const NumberType = number({ int: true });

const LineItem = shape({
  itemId: string(),
  quantity: string(),
  fulfillmentLocation: string(),
  fulfillmentMethod: string()
});

const ErrorProperties = shape({
  errorProperty: shape({
    name: string(),
    value: string()
  })
});

const Error = shape({
  id: string(),
  description: string(),
  submessage: string(),
  errorCode: string(),
  errorProperties: ErrorProperties
});

const ErrorData = shape({
  errors: arrayOf(Error),
  httpStatus: string(),
  userId: string(),
  customerAccountId: string(),
  correlationId: string(),
  correlationType: string()
});

const QuoteInfo = shape({
  cartId: string(),
  errorData: arrayOf(ErrorData)
});

const Quote = shape({
  name: string(),
  status: string(),
  cartId: string(),
  createdUserId: string(),
  accessType: number(),
  hasApplianceItem: bool()
});

const GetQuote = shape({
  customer: shape({
    customerId: string(),
    userId: string()
  }),
  pagination: shape({
    totalNbrOfQuotes: NumberType,
    totalNbrOfPages: NumberType
  }),
  quotes: arrayOf(Quote)
});

const Location = shape({
  curbsidePickupFlag: bool(),
  isBuyInStoreCheckNearBy: bool(),
  distance: number({ float: true }),
  inventory: shape({
    isOutOfStock: bool(),
    isInStock: bool(),
    isLimitedQuantity: bool(),
    isUnavailable: bool(),
    quantity: number(),
    maxAllowedBopisQty: number(),
    minAllowedBopisQty: number()
  }),
  isAnchor: bool(),
  locationId: string(),
  state: string(),
  storeName: string(),
  storePhone: string(),
  type: string()
});

const Service = shape({
  deliveryTimeline: string(),
  deliveryDates: shape({
    startDate: string(),
    endDate: string()
  }),
  deliveryCharge: string(),
  dynamicEta: shape({
    hours: string(),
    minutes: string()
  }),
  hasFreeShipping: bool(),
  freeDeliveryThreshold: number({ float: true }),
  locations: arrayOf(Location),
  type: string(),
  totalCharge: number({ float: true })
});

const Image = shape({
  url: string(),
  type: string(),
  subType: string()
});

export const dataModel = {
  product: params({
    itemId: string().isRequired(),
    dataSource: string()
  }).shape({
    itemId: string(),
    dataSources: string(),
    identifiers: shape({
      itemId: string(),
      brandName: string(),
      productLabel: string(),
      productType: string()
    }),
    media: shape({ images: arrayOf(Image) }),
    info: shape({
      hidePrice: bool(),
      ecoRebate: bool(),
      quantityLimit: number(),
      categoryHierarchy: arrayOf(string()),
      sskMin: string(),
      sskMax: string(),
      unitOfMeasureCoverage: string(),
      wasMaxPriceRange: number({ float: true }),
      wasMinPriceRange: number({ float: true }),
      productSubType: shape({
        name: string()
      })
    }),
    pricing: params({
      storeId: string()
    }).shape({
      value: number({ float: true }),
      original: number({ float: true }),
      specialBuy: number({ float: true }),
      unitOfMeasure: string(),
      message: string(),
      alternatePriceDisplay: bool(),
      mapAboveOriginalPrice: bool(),
      preferredPriceFlag: bool(),
      mapDetail: shape({
        percentageOff: number({ float: true }),
        dollarOff: number({ float: true }),
        effectiveMap: number({ float: true }),
        mapPolicy: string(),
        mapOriginalPriceViolation: bool(),
        mapSpecialPriceViolation: bool()
      }),
      alternate: shape({
        bulk: shape({
          pricePerUnit: number({ float: true }),
          thresholdQuantity: number(),
          value: number({ float: true })
        }),
        unit: shape({
          caseUnitOfMeasure: string(),
          unitsOriginalPrice: number({ float: true }),
          unitsPerCase: number({ float: true }),
          value: number({ float: true })
        })
      }),
      promotion: shape({
        type: string(),
        description: shape({
          shortDesc: string(),
          longDesc: string()
        }),
        dollarOff: number({ float: true }),
        percentageOff: number({ float: true }),
        promotionTag: string(),
        savingsCenter: string(),
        savingsCenterPromos: string(),
        specialBuySavings: string(),
        specialBuyDollarOff: number({ float: true }),
        specialBuyPercentageOff: number({ float: true })
      })
    }),
    taxonomy: shape({
      breadCrumbs: arrayOf(shape({
        label: string()
      }))
    }),
    fulfillment: client(
      params({
        storeId: string(),
        zipCode: string()
      }).shape({
        anchorStoreStatus: bool(),
        anchorStoreStatusType: string(),
        backordered: bool(),
        backorderedShipDate: string(),
        bossExcludedShipStates: string(),
        excludedShipStates: string(),
        seasonStatusEligible: bool(),
        fulfillmentOptions: arrayOf(
          shape({
            type: string(),
            fulfillable: bool(),
            services: arrayOf(Service)
          })
        ),
        onlineStoreStatus: bool(),
        onlineStoreStatusType: string()
      })
    )
  }),

  quoteList: params({
    svocId: string().isRequired(),
    userId: string(),
    pageNumber: string(),
    pageSize: string(),
    quoteStatus: string(),
    sortBy: string(),
    sortOrder: string(),
    projectId: string()
  }).shape(GetQuote),

  createQuote: params({
    svocId: string().isRequired(),
    userId: string().isRequired(),
    createQuoteRequest: customType('QuoteRequest')
      .shape({
        localStoreId: string().isRequired(),
        cartName: string().isRequired(),
        deliveryZip: string(),
        lineItems: arrayOf(LineItem),
        projectId: string()
      })
      .isRequired()
  })
    .mutation()
    .shape(QuoteInfo),

  addToQuote: params({
    svocId: string().isRequired(),
    userId: string().isRequired(),
    addToQuoteRequest: customType('QuoteRequest')
      .shape({
        localStoreId: string().isRequired(),
        quoteId: string().isRequired(),
        deliveryZip: string(),
        lineItems: arrayOf(LineItem),
        projectId: string()
      })
      .isRequired()
  })
    .mutation()
    .shape(QuoteInfo)
};